import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, ShieldAlert, Loader2, Image as ImageIcon, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import ChannelTabs from "../components/ChannelTabs";
import MessageBubble from "../components/MessageBubble";
import EmojiPicker from "../components/EmojiPicker";
import api, { formatApiErrorDetail, uploadImage } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Chat() {
  const { user, refresh } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(searchParams.get("channel") || "allgemein");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [blockedNote, setBlockedNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  const activeChannel = channels.find((c) => c.id === active);

  // Sync URL ?channel=... with local state
  useEffect(() => {
    const ch = searchParams.get("channel");
    if (ch && ch !== active) setActive(ch);
  }, [searchParams]);  // eslint-disable-line react-hooks/exhaustive-deps

  const selectChannel = (id) => {
    setActive(id);
    setSearchParams({ channel: id }, { replace: true });
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/channels");
        setChannels(data);
      } catch (err) {
        console.error("Failed to load channels", err);
      }
    })();
  }, []);

  const fetchMessages = useCallback(async (channelId, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/messages?channel=${encodeURIComponent(channelId)}`);
      setMessages(data);
    } catch (err) {
      console.error("Failed to load messages", err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages(active);
    setBlockedNote("");
  }, [active, fetchMessages]);

  useEffect(() => {
    const id = setInterval(() => fetchMessages(active, true), 3000);
    return () => clearInterval(id);
  }, [active, fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, active]);

  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Bitte nur Bilder auswählen.");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Bild ist zu groß (max. 5 MB).");
      return;
    }
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const send = async (e) => {
    e.preventDefault();
    const content = text.trim();
    if ((!content && !imageFile) || sending) return;
    setSending(true);
    setBlockedNote("");
    try {
      let image_url = null;
      if (imageFile) {
        const up = await uploadImage(imageFile);
        image_url = up.url;
      }
      const { data } = await api.post("/messages", {
        channel: active,
        content: content || "📷 Bild",
        image_url,
      });
      setMessages((prev) => [...prev, data]);
      setText("");
      clearImage();
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setBlockedNote(detail);
      toast.error("Nachricht blockiert", { description: detail });
      // refresh user state in case warnings/ban changed
      refresh();
    } finally {
      setSending(false);
    }
  };

  const warningsLeft = Math.max(0, 3 - (user?.warnings || 0));

  return (
    <div data-testid="chat-page" className="flex flex-col gap-3 sm:gap-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] text-[#4DBFFF] mb-1">Community-Chat</p>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tighter truncate">
            #{activeChannel?.name || "Allgemein"}
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 mt-0.5 sm:mt-1 truncate">
            {activeChannel?.description || "Diskutiere mit anderen Fahrern."}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs shrink-0">
          {user?.warnings > 0 && (
            <div className="flex items-center gap-1 text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2 py-1 rounded-full text-[10px]" data-testid="warnings-badge">
              <AlertTriangle className="w-3 h-3" />
              {user.warnings}/3
            </div>
          )}
          <div className="hidden sm:flex items-center gap-1.5 text-slate-500 text-[11px]">
            <ShieldAlert className="w-3.5 h-3.5 text-[#4DBFFF]" />
            KI-Moderation
          </div>
        </div>
      </div>

      <ChannelTabs channels={channels} active={active} onChange={selectChannel} />

      <div className="bg-[#FFFFFF] border border-slate-200 rounded-2xl overflow-hidden flex flex-col h-[calc(100vh-360px)] sm:h-[calc(100vh-380px)] min-h-[320px]">
        <div className="flex-1 overflow-y-auto p-4 md:p-6" data-testid="messages-container">
          {loading ? (
            <div className="h-full flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Nachrichten…
            </div>
          ) : messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-slate-500 px-6">
              <div className="w-16 h-16 rounded-full bg-[#1D5BFF]/10 border border-[#1D5BFF]/30 flex items-center justify-center mb-4">
                <Send className="w-6 h-6 text-[#4DBFFF]" />
              </div>
              <p className="font-display text-lg text-slate-900">Noch keine Nachrichten</p>
              <p className="text-sm mt-1">Sei der Erste, der in #{activeChannel?.name} schreibt.</p>
            </div>
          ) : (
            messages.map((m) => (
              <MessageBubble
                key={m.id}
                msg={m}
                isOwn={user && m.user_id === user.id}
                canDelete={user && (user.role === "admin" || m.user_id === user.id)}
                onDelete={async (msg) => {
                  if (!window.confirm("Nachricht wirklich löschen?")) return;
                  try {
                    await api.delete(`/messages/${msg.id}`);
                    setMessages((prev) => prev.filter((x) => x.id !== msg.id));
                    toast.success("Gelöscht");
                  } catch (err) {
                    toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler");
                  }
                }}
              />
            ))
          )}
          <div ref={bottomRef} />
        </div>

        {blockedNote && (
          <div
            className="mx-4 mb-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/40 text-red-300 text-sm flex items-start gap-3"
            data-testid="blocked-banner"
          >
            <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-red-200">Nachricht nicht veröffentlicht</div>
              <div className="text-red-300/90">{blockedNote}</div>
              {warningsLeft <= 1 && warningsLeft > 0 && (
                <div className="text-amber-300 text-xs mt-1">
                  Letzte Warnung – die nächste Verletzung führt zur Sperre.
                </div>
              )}
            </div>
          </div>
        )}

        {imagePreview && (
          <div className="mx-4 mb-2 relative inline-block">
            <img src={imagePreview} alt="Vorschau" className="h-20 rounded-lg border border-slate-200" />
            <button
              type="button"
              onClick={clearImage}
              data-testid="remove-image-btn"
              className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-slate-900 rounded-full p-1"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        <form
          onSubmit={send}
          className="border-t border-slate-200 bg-[#F5F8FD] p-3 flex items-end gap-2"
          data-testid="message-composer"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onPickImage}
            className="hidden"
            data-testid="image-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            data-testid="attach-image-btn"
            className="p-3 rounded-2xl bg-[#FFFFFF] border border-slate-200 text-slate-700 hover:text-slate-900 hover:border-[#1D5BFF]/50 transition-all"
            title="Bild anhängen"
          >
            <ImageIcon className="w-5 h-5" />
          </button>
          <EmojiPicker onPick={(e) => setText((t) => t + e)} />
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(e);
              }
            }}
            rows={1}
            placeholder={`Nachricht in #${activeChannel?.name || ""}…`}
            data-testid="message-input"
            className="flex-1 resize-none bg-[#FFFFFF] border border-slate-200 rounded-2xl px-4 py-3 text-slate-900 placeholder:text-slate-600 focus:outline-none focus:border-[#1D5BFF] focus:ring-2 focus:ring-[#1D5BFF]/30 transition-all max-h-32"
          />
          <button
            type="submit"
            disabled={sending || (!text.trim() && !imageFile)}
            data-testid="send-message-btn"
            className="bg-[#1D5BFF] hover:bg-[#1546C9] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold p-3 rounded-full transition-all active:scale-95 shadow-[0_8px_30px_rgba(0,102,255,0.35)]"
            title="Senden"
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </form>
      </div>
    </div>
  );
}
